import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import type { AdsAdvertiserItem } from '@brand24-original/llm-types';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import { AdsErrorPanel, AdvertiserFavicon } from './AdsTableParts';

/**
 * Rows shown before "Show more": header + 8 rows end level with the chart panel next to it
 * — an unbounded list would overflow below the chart with its own scrollbar.
 */
const COLLAPSED_ROWS = 8;

interface AdShareTableProps {
  advertisers: AdsAdvertiserItem[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  colorForAdvertiser: (domain: string) => string;
}

const AdShareTable: React.FC<AdShareTableProps> = ({
  advertisers,
  isLoading,
  isError,
  onRetry,
  colorForAdvertiser,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hiddenCount = advertisers.length - COLLAPSED_ROWS;
  const canCollapse = hiddenCount > 0;
  const rows = expanded || !canCollapse ? advertisers : advertisers.slice(0, COLLAPSED_ROWS);

  return (
    <div className="stats__panel ads-page__share-panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('ads.shareTitle')}
          <CustomTooltip tooltipText="ads.shareDesc" addMargin={false} />
        </div>
        {!isLoading && advertisers.length > 0 && (
          <span className="ads-page__panel-count">
            {t('ads.advertisersCount', { count: advertisers.length })}
          </span>
        )}
      </div>
      {isLoading ? (
        <Skeleton className="skeleton--table skeleton--table-5">
          <span />
          <span />
          <span />
          <span />
          <span />
        </Skeleton>
      ) : isError ? (
        <AdsErrorPanel framed onRetry={onRetry} />
      ) : (
        <>
          <DataTable
            value={rows}
            dataKey="domain"
            // Fixed layout: a long advertiser name truncates inside its cell instead of widening the
            // table past the panel (which showed a horizontal scrollbar). Column widths are content-box
            // (+ cell padding, compacted in AdsPage.scss) — the name column takes the rest.
            tableStyle={{ width: '100%', tableLayout: 'fixed' }}
            tableClassName="table"
            // Expanded, the rows scroll INSIDE the panel — it never outgrows the chart card next to it.
            // Telefon: karty (reklamodawca jako tytuł, udział / wyświetlenia / prompty w siatce).
            className={`table-wrapper table-wrapper--stack ${expanded ? 'ads-page__share-scroll' : ''}`}
            responsiveLayout="stack"
            emptyMessage={() => <EmptyPanel textKey="ads.emptyTitle" descriptionKey="ads.emptyDesc" />}
          >
            <Column
              header={t('ads.colAdvertiser')}
              headerClassName="table__header"
              bodyClassName="table__cell table__cell--stack-title"
              body={(row: AdsAdvertiserItem) => (
                // Color dot = the advertiser's series in the By-advertiser chart; favicon = identity
                // (same dot + favicon + name legend as the Sources citation trends).
                <div className="ads-page__advertiser-cell">
                  <span
                    className="ads-page__dot"
                    style={{ backgroundColor: colorForAdvertiser(row.domain) }}
                  />
                  <AdvertiserFavicon faviconUrl={row.faviconUrl} domain={row.domain} />
                  <span className="ads-page__advertiser-name" title={row.domain}>
                    {row.advertiser}
                  </span>
                  {row.isYou && <span className="ads-page__you-badge">{t('ads.youBadge')}</span>}
                </div>
              )}
            />
            <Column
              header={t('ads.colShare')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell table__cell--center"
              style={{ width: '52px' }}
              body={(row: AdsAdvertiserItem) => `${row.share.toFixed(1)}%`}
            />
            <Column
              header={t('ads.colSeen')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell table__cell--center"
              style={{ width: '64px' }}
              body={(row: AdsAdvertiserItem) => row.occurrences}
            />
            <Column
              header={t('ads.colPrompts')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell table__cell--center"
              style={{ width: '52px' }}
              body={(row: AdsAdvertiserItem) => row.prompts}
            />
          </DataTable>
          {canCollapse && (
            <button
              type="button"
              className="show-more-toggle"
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
            >
              <span className={`ico--bottom ico--xs ${expanded ? 'ico--rotate' : ''}`} aria-hidden="true" />
              {expanded ? t('ads.showLess') : t('ads.showMore', { count: hiddenCount })}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AdShareTable;
